import { useState, useEffect, useCallback } from 'react';
import { Settings, Save, Calendar, Percent, DollarSign, Users, Clock, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingSettingsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  
  // Booking Settings
  const [bookingSettings, setBookingSettings] = useState({
    blackout_days: 2,
    discount_slabs: [
      { duration_months: 1, discount_percent: 0 },
      { duration_months: 3, discount_percent: 5 },
      { duration_months: 6, discount_percent: 10 },
      { duration_months: 12, discount_percent: 15 }
    ],
    booking_number_prefix: 'BKG'
  });
  
  // Staff Settings
  const [staffSettings, setStaffSettings] = useState({
    our_staff_rate: 0,
    their_staff_rate: 0,
    staff_tax_percent: 18,
    min_contract_months: 1
  });

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const [bookingRes, staffRes] = await Promise.all([
        fetch(`${API_URL}/api/bookings/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/bookings/staff-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      if (bookingRes.ok) {
        const data = await bookingRes.json();
        setBookingSettings({
          blackout_days: data.blackout_days || 2,
          discount_slabs: data.discount_slabs || [],
          booking_number_prefix: data.booking_number_prefix || 'BKG'
        });
      }
      
      if (staffRes.ok) {
        const data = await staffRes.json();
        setStaffSettings({
          our_staff_rate: data.our_staff_rate || 0,
          their_staff_rate: data.their_staff_rate || 0,
          staff_tax_percent: data.staff_tax_percent || 18,
          min_contract_months: data.min_contract_months || 1
        });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch settings', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Update discount slab
  const updateDiscountSlab = (index, value) => {
    const newSlabs = [...bookingSettings.discount_slabs];
    newSlabs[index] = { ...newSlabs[index], discount_percent: parseFloat(value) || 0 };
    setBookingSettings(p => ({ ...p, discount_slabs: newSlabs }));
  };

  // Save booking settings
  const saveBookingSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem('access_token');
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(bookingSettings)
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Booking settings saved' });
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to save', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // Save staff settings
  const saveStaffSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem('access_token');
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/staff-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(staffSettings)
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Staff settings saved' });
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to save', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to save settings', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="booking-settings-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Booking Settings</h1>
          <p className="text-gray-500 text-sm">Configure shelf booking and brand staff hiring settings</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="general" className="data-[state=active]:bg-white">
            <Settings className="w-4 h-4 mr-2" /> General Settings
          </TabsTrigger>
          <TabsTrigger value="staff" className="data-[state=active]:bg-white">
            <Users className="w-4 h-4 mr-2" /> Brand Staff Rates
          </TabsTrigger>
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Blackout Period */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  Blackout Period
                </CardTitle>
                <CardDescription>
                  Minimum gap required between consecutive bookings for the same cell/display
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={bookingSettings.blackout_days}
                    onChange={(e) => setBookingSettings(p => ({ ...p, blackout_days: parseInt(e.target.value) || 0 }))}
                    className="w-24 bg-gray-50 border-gray-300"
                  />
                  <span className="text-gray-600">days</span>
                </div>
                <p className="text-sm text-gray-500">
                  Set to 0 for back-to-back bookings. Default is 2 days.
                </p>
              </CardContent>
            </Card>

            {/* Booking Number Prefix */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-purple-500" />
                  Booking Number Prefix
                </CardTitle>
                <CardDescription>
                  Prefix for booking numbers (e.g., BKG-2026-00001)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Input
                  value={bookingSettings.booking_number_prefix}
                  onChange={(e) => setBookingSettings(p => ({ ...p, booking_number_prefix: e.target.value.toUpperCase() }))}
                  className="w-32 bg-gray-50 border-gray-300"
                  maxLength={5}
                />
                <p className="text-sm text-gray-500">
                  Example: {bookingSettings.booking_number_prefix}-2026-00001
                </p>
              </CardContent>
            </Card>

            {/* Discount Slabs */}
            <Card className="bg-white border-gray-200 lg:col-span-2">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Percent className="w-5 h-5 text-green-500" />
                  Duration Discount Slabs
                </CardTitle>
                <CardDescription>
                  Automatic discounts applied based on booking/contract duration
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {bookingSettings.discount_slabs.map((slab, idx) => (
                    <div key={slab.duration_months} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-sm font-medium text-gray-600 mb-2">
                        {slab.duration_months} Month{slab.duration_months > 1 ? 's' : ''}
                      </p>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          value={slab.discount_percent}
                          onChange={(e) => updateDiscountSlab(idx, e.target.value)}
                          className="w-20 bg-white border-gray-300"
                        />
                        <span className="text-gray-600">%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                  <p className="text-sm text-green-700">
                    <strong>Example:</strong> A 6-month booking with {bookingSettings.discount_slabs.find(s => s.duration_months === 6)?.discount_percent || 0}% discount 
                    will have the same discounted rate applied to all 6 monthly invoices.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex justify-end">
            <Button onClick={saveBookingSettings} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save General Settings'}
            </Button>
          </div>
        </TabsContent>

        {/* Staff Settings Tab */}
        <TabsContent value="staff" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Our Staff Rate */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-indigo-500" />
                  Our Staff Rate
                </CardTitle>
                <CardDescription>
                  Monthly rate per person when we provide the marketing staff
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">₹</span>
                  <Input
                    type="number"
                    min="0"
                    value={staffSettings.our_staff_rate}
                    onChange={(e) => setStaffSettings(p => ({ ...p, our_staff_rate: parseFloat(e.target.value) || 0 }))}
                    className="w-40 bg-gray-50 border-gray-300"
                  />
                  <span className="text-gray-600">/ person / month</span>
                </div>
                <p className="text-sm text-gray-500">
                  This rate applies when we provide trained marketing staff to the manufacturer.
                </p>
              </CardContent>
            </Card>

            {/* Their Staff Rate */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-amber-500" />
                  Their Staff Rate
                </CardTitle>
                <CardDescription>
                  Monthly supervision fee per person when manufacturer provides staff
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <span className="text-gray-600">₹</span>
                  <Input
                    type="number"
                    min="0"
                    value={staffSettings.their_staff_rate}
                    onChange={(e) => setStaffSettings(p => ({ ...p, their_staff_rate: parseFloat(e.target.value) || 0 }))}
                    className="w-40 bg-gray-50 border-gray-300"
                  />
                  <span className="text-gray-600">/ person / month</span>
                </div>
                <p className="text-sm text-gray-500">
                  This rate applies when the manufacturer brings their own staff (supervision fee).
                </p>
              </CardContent>
            </Card>

            {/* Tax & Contract Settings */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Percent className="w-5 h-5 text-purple-500" />
                  Tax on Staff Hiring
                </CardTitle>
                <CardDescription>
                  GST percentage applied to staff hiring charges
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="50"
                    value={staffSettings.staff_tax_percent}
                    onChange={(e) => setStaffSettings(p => ({ ...p, staff_tax_percent: parseFloat(e.target.value) || 0 }))}
                    className="w-24 bg-gray-50 border-gray-300"
                  />
                  <span className="text-gray-600">%</span>
                </div>
              </CardContent>
            </Card>

            {/* Min Contract Months */}
            <Card className="bg-white border-gray-200">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg text-gray-800 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-blue-500" />
                  Minimum Contract Duration
                </CardTitle>
                <CardDescription>
                  Minimum months required for staff hiring contracts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="1"
                    max="12"
                    value={staffSettings.min_contract_months}
                    onChange={(e) => setStaffSettings(p => ({ ...p, min_contract_months: parseInt(e.target.value) || 1 }))}
                    className="w-24 bg-gray-50 border-gray-300"
                  />
                  <span className="text-gray-600">months</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Rate Summary */}
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-indigo-600 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-indigo-800 mb-2">Rate Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Our Staff (1 person)</p>
                      <p className="font-medium text-indigo-700">
                        ₹{(staffSettings.our_staff_rate * (1 + staffSettings.staff_tax_percent / 100)).toFixed(0)}/month
                      </p>
                      <p className="text-xs text-gray-500">incl. {staffSettings.staff_tax_percent}% tax</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Their Staff (1 person)</p>
                      <p className="font-medium text-indigo-700">
                        ₹{(staffSettings.their_staff_rate * (1 + staffSettings.staff_tax_percent / 100)).toFixed(0)}/month
                      </p>
                      <p className="text-xs text-gray-500">incl. {staffSettings.staff_tax_percent}% tax</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Our Staff (2 persons, 6mo)</p>
                      <p className="font-medium text-indigo-700">
                        ₹{(staffSettings.our_staff_rate * 2 * (1 + staffSettings.staff_tax_percent / 100) * 6).toLocaleString()}/total
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Their Staff (2 persons, 6mo)</p>
                      <p className="font-medium text-indigo-700">
                        ₹{(staffSettings.their_staff_rate * 2 * (1 + staffSettings.staff_tax_percent / 100) * 6).toLocaleString()}/total
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={saveStaffSettings} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700">
              <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Staff Settings'}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default BookingSettingsPage;
